import { useEffect, useState } from "react";
import { crDate } from "@/lib/dateFormat";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/i18n";
import PageHeader from "@/components/PageHeader";
import { BrandAvatar } from "@/components/BrandAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { UserCircle2, Loader2, Save, Mail, ShieldCheck, BookOpen, Wallet, MapPin, Video, Camera, Trash2, Radio } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  return crDate(iso) || iso;
}

export default function Profile() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const [saving, setSaving] = useState(false);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const isStudent = user?.role === "student";
  const isTeacher = user?.role === "teacher";
  const isMeetingHost = ["superadmin", "academic_director"].includes(user?.role);

  const onAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so the same file can be chosen again
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("avatar_too_large"));
      return;
    }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error(t("avatar_invalid_type"));
      return;
    }
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success(t("avatar_updated"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("avatar_upload_error"));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const onAvatarRemove = async () => {
    setUploadingAvatar(true);
    try {
      await api.delete("/users/me/avatar");
      await refresh();
      toast.success(t("avatar_removed"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("avatar_upload_error"));
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Editable copy of user fields (whitelist)
  const [form, setForm] = useState({});
  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      whatsapp: user.whatsapp || "",
      country: user.country || "",
      country_of_residence: user.country_of_residence || "",
      city: user.city || "",
      date_of_birth: user.date_of_birth || "",
      occupation: user.occupation || "",
      native_language: user.native_language || "",
      learning_goal: user.learning_goal || "",
      address: user.address || "",
      profession: user.profession || "",
      emergency_contact_name: user.emergency_contact_name || "",
      emergency_contact_phone: user.emergency_contact_phone || "",
      meet_link: user.meet_link || "",
      zoom_link: user.zoom_link || "",
    });
  }, [user]);

  // Student dashboard payload (enrollments + next pending)
  useEffect(() => {
    if (!isStudent) return;
    let cancelled = false;
    setLoadingDashboard(true);
    api.get("/me/dashboard")
      .then((r) => !cancelled && setDashboard(r.data))
      .catch(() => {})
      .finally(() => !cancelled && setLoadingDashboard(false));
    return () => { cancelled = true; };
  }, [isStudent]);

  // Avisos por correo de WSS Live (se guardan al instante)
  const [livePrefs, setLivePrefs] = useState({ weekly_digest: true, daily_digest: true });
  useEffect(() => {
    if (!isStudent) return;
    api.get("/live-classes/email-preferences").then((r) => setLivePrefs(r.data)).catch(() => {});
  }, [isStudent]);

  const saveLivePref = async (key, value) => {
    setLivePrefs((p) => ({ ...p, [key]: value }));
    try {
      const r = await api.patch("/live-classes/email-preferences", { [key]: value });
      setLivePrefs(r.data);
      toast.success(t("profile_saved"));
    } catch (e) {
      setLivePrefs((p) => ({ ...p, [key]: !value }));
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const onSave = async () => {
    setSaving(true);
    try {
      // Send every field: an empty value clears it on the server
      const payload = Object.fromEntries(
        Object.entries(form)
          .filter(([k, v]) => k !== "name" || (v && v.trim()))
          .map(([k, v]) => [k, v === "" || v == null ? "" : v])
      );
      await api.patch("/users/me", payload);
      await refresh();
      toast.success(t("profile_saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="profile-page">
      <PageHeader icon={UserCircle2} title={t("my_profile")} />

      {/* Identity card */}
      <Card className="p-6 border-slate-200">
        <div className="flex items-start gap-5">
          <div className="relative group shrink-0">
            <BrandAvatar src={user.picture} alt={user.name} className="h-20 w-20 text-2xl" />
            <button
              type="button"
              data-testid="profile-avatar-change-btn"
              onClick={() => document.getElementById("profile-avatar-input")?.click()}
              disabled={uploadingAvatar}
              className="absolute inset-0 rounded-full flex items-center justify-center bg-black/0 hover:bg-black/50 transition-colors cursor-pointer"
              aria-label={t("change_avatar")}
            >
              {uploadingAvatar ? (
                <Loader2 className="h-6 w-6 text-white animate-spin opacity-100" />
              ) : (
                <Camera className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              )}
            </button>
            <input
              id="profile-avatar-input"
              data-testid="profile-avatar-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={onAvatarChange}
              className="hidden"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h2 className="text-xl font-bold text-slate-900 truncate" data-testid="profile-name">
                {user.name || "—"}
              </h2>
              <Badge variant="outline" className="text-xs">{t(user.role)}</Badge>
              {isStudent && user.student_status && (
                <Badge className={
                  user.student_status === "active" ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
                  user.student_status === "inactive" ? "bg-amber-100 text-amber-700 border-amber-200" :
                  "bg-rose-100 text-rose-700 border-rose-200"
                }>
                  {t(`status_${user.student_status}`)}
                </Badge>
              )}
            </div>
            <div className="text-sm text-slate-600 flex items-center gap-1.5 mb-2">
              <Mail className="h-3.5 w-3.5" /> {user.email}
            </div>
            <div className="flex flex-wrap gap-2 mb-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid="profile-avatar-upload-btn"
                onClick={() => document.getElementById("profile-avatar-input")?.click()}
                disabled={uploadingAvatar}
                className="h-7 text-xs"
              >
                <Camera className="h-3 w-3 mr-1.5" />
                {user.picture_path ? t("change_avatar") : t("upload_avatar")}
              </Button>
              {user.picture_path && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  data-testid="profile-avatar-remove-btn"
                  onClick={onAvatarRemove}
                  disabled={uploadingAvatar}
                  className="h-7 text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                >
                  <Trash2 className="h-3 w-3 mr-1.5" />
                  {t("remove_avatar")}
                </Button>
              )}
            </div>
            <div className="text-[11px] text-slate-400 flex items-center gap-1.5">
              <ShieldCheck className="h-3 w-3" /> {t("profile_readonly_note")}
            </div>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList data-testid="profile-tabs">
          <TabsTrigger value="profile" data-testid="profile-tab-edit">{t("profile_tab_personal")}</TabsTrigger>
          {isStudent && (
            <TabsTrigger value="enrollments" data-testid="profile-tab-enrollments">{t("profile_tab_enrollments")}</TabsTrigger>
          )}
        </TabsList>

        {/* Personal info form */}
        <TabsContent value="profile" className="space-y-4">
          <Card className="p-6 border-slate-200">
            <h3 className="text-sm font-semibold text-slate-900 mb-4">{t("profile_section_basic")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pf-name" className="text-xs">{t("name")}</Label>
                <Input id="pf-name" data-testid="profile-input-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="pf-wa" className="text-xs">{t("whatsapp")}</Label>
                <Input id="pf-wa" data-testid="profile-input-whatsapp" value={form.whatsapp || ""} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+506 8888 7777" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="pf-dob" className="text-xs">{t("date_of_birth")}</Label>
                <Input id="pf-dob" data-testid="profile-input-dob" type="date" value={form.date_of_birth || ""} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="pf-cor" className="text-xs">{t("country_of_residence")}</Label>
                <Input id="pf-cor" data-testid="profile-input-country" value={form.country_of_residence || ""} onChange={(e) => setForm({ ...form, country_of_residence: e.target.value })} placeholder="CR" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="pf-city" className="text-xs">{t("city")}</Label>
                <Input id="pf-city" data-testid="profile-input-city" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5" />
              </div>
            </div>
          </Card>

          {isStudent && (
            <Card className="p-6 border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">{t("profile_section_learner")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pf-occ" className="text-xs">{t("occupation")}</Label>
                  <Input id="pf-occ" data-testid="profile-input-occupation" value={form.occupation || ""} onChange={(e) => setForm({ ...form, occupation: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="pf-nat" className="text-xs">{t("native_language")}</Label>
                  <Input id="pf-nat" data-testid="profile-input-native-lang" value={form.native_language || ""} onChange={(e) => setForm({ ...form, native_language: e.target.value })} className="mt-1.5" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="pf-goal" className="text-xs">{t("learning_goal")}</Label>
                  <Input id="pf-goal" data-testid="profile-input-goal" value={form.learning_goal || ""} onChange={(e) => setForm({ ...form, learning_goal: e.target.value })} className="mt-1.5" />
                </div>
              </div>
            </Card>
          )}

          {isStudent && (
            <Card className="p-6 border-slate-200" data-testid="live-email-prefs-card">
              <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
                <Radio className="h-4 w-4 text-brand-blue" /> {t("live_email_prefs_title")}
              </h3>
              <p className="text-xs text-slate-500 mb-4">{t("live_email_prefs_hint")}</p>
              <div className="space-y-3">
                {[
                  { key: "weekly_digest", testid: "live-pref-weekly", label: "live_email_weekly", hint: "live_email_weekly_hint" },
                  { key: "daily_digest", testid: "live-pref-daily", label: "live_email_daily", hint: "live_email_daily_hint" },
                ].map((row) => (
                  <div key={row.key} className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 p-3">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{t(row.label)}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{t(row.hint)}</div>
                    </div>
                    <Switch
                      data-testid={row.testid}
                      checked={!!livePrefs[row.key]}
                      onCheckedChange={(v) => saveLivePref(row.key, v)}
                    />
                  </div>
                ))}
              </div>
            </Card>
          )}

          {isTeacher && (
            <Card className="p-6 border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">{t("profile_section_teacher")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pf-prof" className="text-xs">{t("profession")}</Label>
                  <Input id="pf-prof" data-testid="profile-input-profession" value={form.profession || ""} onChange={(e) => setForm({ ...form, profession: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="pf-addr" className="text-xs">{t("address")}</Label>
                  <Input id="pf-addr" data-testid="profile-input-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="pf-ec-name" className="text-xs">{t("emergency_contact_name")}</Label>
                  <Input id="pf-ec-name" data-testid="profile-input-emergency-name" value={form.emergency_contact_name || ""} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="pf-ec-phone" className="text-xs">{t("emergency_contact_phone")}</Label>
                  <Input id="pf-ec-phone" data-testid="profile-input-emergency-phone" value={form.emergency_contact_phone || ""} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} className="mt-1.5" />
                </div>
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-slate-100">
                  <div>
                    <Label htmlFor="pf-meet" className="text-xs flex items-center gap-1.5"><Video className="h-3 w-3" /> Google Meet</Label>
                    <Input id="pf-meet" data-testid="profile-input-meet" value={form.meet_link || ""} onChange={(e) => setForm({ ...form, meet_link: e.target.value })} placeholder="https://meet.google.com/..." className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="pf-zoom" className="text-xs flex items-center gap-1.5"><Video className="h-3 w-3" /> Zoom</Label>
                    <Input id="pf-zoom" data-testid="profile-input-zoom" value={form.zoom_link || ""} onChange={(e) => setForm({ ...form, zoom_link: e.target.value })} placeholder="https://zoom.us/j/..." className="mt-1.5" />
                  </div>
                </div>
              </div>
            </Card>
          )}

          {isMeetingHost && (
            <Card className="p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-1">{t("meeting_room_section")}</h3>
              <p className="text-xs text-slate-500 mb-4">{t("meeting_room_hint")}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pf-room-meet" className="text-xs flex items-center gap-1.5"><Video className="h-3 w-3" /> Google Meet</Label>
                  <Input id="pf-room-meet" data-testid="profile-input-room-meet" value={form.meet_link || ""} onChange={(e) => setForm({ ...form, meet_link: e.target.value })} placeholder="https://meet.google.com/..." className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="pf-room-zoom" className="text-xs flex items-center gap-1.5"><Video className="h-3 w-3" /> Zoom</Label>
                  <Input id="pf-room-zoom" data-testid="profile-input-room-zoom" value={form.zoom_link || ""} onChange={(e) => setForm({ ...form, zoom_link: e.target.value })} placeholder="https://zoom.us/j/..." className="mt-1.5" />
                </div>
              </div>
            </Card>
          )}

          <div className="flex justify-end pt-2">
            <Button data-testid="profile-save-btn" onClick={onSave} disabled={saving} className="bg-brand-blue hover:bg-brand-blue/90 text-white">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Save className="h-4 w-4 mr-2" />
              {t("save_changes")}
            </Button>
          </div>
        </TabsContent>

        {/* Student-only enrollments tab */}
        {isStudent && (
          <TabsContent value="enrollments" className="space-y-4">
            {loadingDashboard ? (
              <Card className="p-10 border-slate-200 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </Card>
            ) : (
              <>
                {dashboard?.next_pending_payment && (
                  <Card className="p-5 border-amber-300 bg-amber-50/60">
                    <div className="flex items-start gap-3">
                      <Wallet className="h-5 w-5 text-amber-700 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-xs uppercase tracking-wider font-semibold text-amber-800">{t("next_pending_payment")}</div>
                        <div className="text-lg font-bold text-slate-900 mt-1" data-testid="profile-next-payment-amount">
                          ${Number(dashboard.next_pending_payment.amount || 0).toFixed(2)} USD
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5">
                          {t("due_date")}: <strong>{fmtDate(dashboard.next_pending_payment.due_date)}</strong>
                        </div>
                      </div>
                    </div>
                  </Card>
                )}

                {(dashboard?.enrollments || []).length === 0 ? (
                  <Card className="p-10 border-slate-200 text-center text-sm text-slate-500" data-testid="profile-no-enrollments">
                    {t("no_enrollments_yet")}
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {dashboard.enrollments.map((e) => {
                      const cycle = e.cycle_length || 0;
                      const consumed = e.latest_payment?.classes_consumed ?? 0;
                      const pct = cycle ? Math.min(100, Math.round((consumed / cycle) * 100)) : 0;
                      return (
                        <Card key={e.id} className="p-5 border-slate-200" data-testid={`profile-enr-${e.id}`}>
                          <div className="flex items-start gap-2 mb-2">
                            <BookOpen className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-slate-900">{e.program_name || "—"}</div>
                              <div className="text-xs text-slate-600 flex items-center gap-1 mt-0.5">
                                <MapPin className="h-3 w-3" /> {e.sede_name || "—"} · {e.group_name || "—"}
                              </div>
                            </div>
                            {e.suspended && (
                              <Badge variant="outline" className="border-rose-300 text-rose-700 text-[10px]">
                                {t("suspended")}
                              </Badge>
                            )}
                          </div>
                          <div className="mt-3 space-y-1.5">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-slate-600">{t("cycle_progress")}</span>
                              <span className="font-semibold text-slate-900">{consumed} / {cycle}</span>
                            </div>
                            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-blue-500 to-blue-400" style={{ width: `${pct}%` }} />
                            </div>
                            {e.start_date && (
                              <div className="text-[11px] text-slate-500 pt-1">
                                {t("start_date")}: {fmtDate(e.start_date)}
                              </div>
                            )}
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
